/// <reference types="vite/client" />
// 统一的 API 客户端 / Unified API client for the web UI.
// 功能 / Features:
//  - double-submit CSRF：从 csrf_token cookie 取值，在非 GET/HEAD/OPTIONS 请求上回显 X-CSRF-Token。
//    Reads csrf_token cookie and echoes it as X-CSRF-Token on mutating requests.
//  - credentials: "include"，保证 HttpOnly 会话 cookie 随请求发送。
//    Ensures HttpOnly session cookie is always sent.
//  - 标准信封翻译 / Envelope translation:
//      401 LOGIN_FAILED → ApiError（保留登录错误码）；其它 401 → AuthError
//      401 LOGIN_FAILED → ApiError (preserve login code); other 401 → AuthError
//      402 INSUFFICIENT_CREDIT → InsufficientCreditError
//      409 ACTIVE_SIMULATION_EXISTS → ActiveSimulationError
//      409 ACTIVE_REPORT_GENERATION_EXISTS → ActiveReportGenerationError
//      429 RATE_LIMITED / RATE_LIMITED_HOURLY → RateLimitedError
//      其它非 2xx → ApiError (带 code)

import {
  ActiveReportGenerationError,
  ActiveSimulationError,
  ApiError,
  AuthError,
  InsufficientCreditError,
  RateLimitedError,
} from "@/api/errors";
import { readCookie } from "@/lib/cookies";

export {
  ActiveReportGenerationError,
  ActiveSimulationError,
  ApiError,
  AuthError,
  InsufficientCreditError,
  RateLimitedError,
};

interface ApiClientOptions {
  baseUrl?: string;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? apiBaseUrl();
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "GET" });
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  }

  /**
   * 发送 JSON PATCH 请求 / Send a JSON PATCH request.
   */
  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: "DELETE" });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const method = (init.method ?? "GET").toUpperCase();
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");

    if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
      const csrf = readCookie("csrf_token");
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }

    const resp = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });

    const contentType = resp.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");
    if (resp.status === 401 && !isJson) {
      throw new AuthError();
    }

    // 非 JSON 响应直接视为失败 / Non-JSON responses are always treated as errors.
    // 背景：生产构建如果漏注入 VITE_API_BASE_URL，请求会被 Amplify SPA catch-all
    // 返回 200 + text/html（index.html）。此处必须主动抛错，否则上层会把 HTML
    // string 当作成功响应使用（登录 handler 读 res.user 得到 undefined，UI 无任何
    // 提示却实际未登录；demos handler 访问 body.single 得到 undefined）。
    // Why: if the SPA catch-all returns HTML with HTTP 200 (e.g. when
    // VITE_API_BASE_URL is missing), downstream code will silently treat the
    // HTML string as a success envelope. Fail loud here instead.
    if (!isJson) {
      throw new ApiError(
        resp.status,
        "NON_JSON_RESPONSE",
        `expected JSON response from ${path}, got ${contentType || "unknown content-type"}; check VITE_API_BASE_URL and API routing`,
      );
    }
    const body: unknown = await resp.json().catch(() => ({}));

    const detail = _detail(body);

    // 登录失败是业务错误，必须保留后端 code 供登录表单显示准确提示。
    // Login failure is a business error; preserve the backend code so the form
    // can show the precise inline message.
    if (resp.status === 401 && detail?.code === "LOGIN_FAILED") {
      throw new ApiError(
        resp.status,
        detail.code,
        String(detail.message ?? "Incorrect email or password"),
      );
    }

    // 其它 401 仍表示会话失效或未登录，由上层统一走登录态处理。
    // Other 401 responses still mean missing/expired auth and are handled as
    // auth-state failures by callers.
    if (resp.status === 401) {
      throw new AuthError(String(detail?.message ?? "authentication required"));
    }

    if (resp.status === 402 && detail?.code === "INSUFFICIENT_CREDIT") {
      throw new InsufficientCreditError(
        Number(detail.cost ?? 0),
        Number(detail.current ?? 0),
        String(detail.message ?? "credit insufficient"),
      );
    }

    if (
      resp.status === 429 &&
      (detail?.code === "RATE_LIMITED" || detail?.code === "RATE_LIMITED_HOURLY")
    ) {
      throw new RateLimitedError(
        Number(detail.retry_after ?? 0),
        detail.code === "RATE_LIMITED_HOURLY",
        String(detail.message ?? "rate limited"),
      );
    }

    if (resp.status === 409 && detail?.code === "ACTIVE_SIMULATION_EXISTS") {
      throw new ActiveSimulationError(String(detail.message ?? "active simulation exists"));
    }

    if (resp.status === 409 && detail?.code === "ACTIVE_REPORT_GENERATION_EXISTS") {
      throw new ActiveReportGenerationError(
        String(detail.message ?? "active report generation exists"),
      );
    }

    if (!resp.ok) {
      throw new ApiError(
        resp.status,
        String(detail?.code ?? "HTTP_ERROR"),
        String(detail?.message ?? resp.statusText ?? "request failed"),
      );
    }

    return body as T;
  }
}

/**
 * 读取 API base URL / Read the configured API base URL.
 */
export function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || "";
}

/**
 * 拼接 API URL / Join a relative API path against VITE_API_BASE_URL.
 */
export function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const base = apiBaseUrl().replace(/\/$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

function _detail(
  body: unknown,
): {
  code?: string;
  message?: string;
  cost?: number | string;
  current?: number | string;
  retry_after?: number | string;
} | null {
  if (body && typeof body === "object" && "detail" in body) {
    const d = (body as Record<string, unknown>).detail;
    if (d && typeof d === "object") {
      return d as Record<string, string>;
    }
  }
  return null;
}

export const api = new ApiClient();
